import { useState, useEffect, useRef, useCallback } from 'react';

interface AnimatedCounterProps {
    value: number;
    formatter: (n: number) => string;
    prefix?: string;
}

/**
 * AnimatedCounter — Two animation modes:
 * 1. On mount: scramble count-up from 0 to target over ~1s
 * 2. On real-time update: slide-up curtain transition
 */
export default function AnimatedCounter({ value, formatter, prefix = '' }: AnimatedCounterProps) {
    const format = useCallback((n: number) => prefix + formatter(n), [prefix, formatter]);

    const [displayText, setDisplayText] = useState(format(value));
    const [slideState, setSlideState] = useState<'idle' | 'sliding'>('idle');
    const [outgoingText, setOutgoingText] = useState('');
    const mountedRef = useRef(false);
    const prevValueRef = useRef(value);
    const targetRef = useRef(value);
    const rafRef = useRef<number | null>(null);

    // Keep targetRef in sync with value during the mount phase
    useEffect(() => {
        targetRef.current = value;
    }, [value]);

    // ── Mount: scramble count-up animation ──
    useEffect(() => {
        const duration = 1000;
        const startTime = performance.now();

        function animate(now: number) {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // Ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            const target = targetRef.current;

            if (progress < 1) {
                const currentNum = Math.round(target * eased);
                const currentFormatted = prefix + formatter(currentNum);

                // Scramble: randomly swap digit characters early on, settle as progress nears 1
                const scrambled = currentFormatted.split('').map((ch) => {
                    if (/\d/.test(ch) && Math.random() > progress * 0.8 + 0.2) {
                        return String(Math.floor(Math.random() * 10));
                    }
                    return ch;
                }).join('');

                setDisplayText(scrambled);
                rafRef.current = requestAnimationFrame(animate);
            } else {
                setDisplayText(format(target));
                prevValueRef.current = target; // Synchronize prevValue to prevent ghost slide on next render
                mountedRef.current = true;
            }
        }

        rafRef.current = requestAnimationFrame(animate);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Real-time update: slide-up curtain ──
    useEffect(() => {
        if (!mountedRef.current) return;
        if (value === prevValueRef.current) return;
        prevValueRef.current = value;

        const newText = format(value);

        // Save current text as outgoing, trigger slide animation
        setOutgoingText(displayText);
        setSlideState('sliding');

        // Small delay so the browser registers the initial state before animating
        requestAnimationFrame(() => {
            setDisplayText(newText);
        });

        // Reset after animation completes
        const timer = setTimeout(() => {
            setSlideState('idle');
            setOutgoingText('');
        }, 450);

        return () => clearTimeout(timer);
    }, [value, format, displayText]);

    return (
        <div className="animated-counter-wrapper">
            {slideState === 'sliding' && outgoingText && (
                <span className="counter-slide counter-slide-out" aria-hidden>
                    {outgoingText}
                </span>
            )}
            <span className={`counter-slide ${slideState === 'sliding' ? 'counter-slide-in' : ''}`}>
                {displayText}
            </span>
        </div>
    );
}
